/**
 * Workflow API + minimal event types (must match server WorkflowEventType discriminators).
 */
import { apiClient } from '@/shared/api/apiClient'

export const WorkflowEventType = {
  WorkflowCompleted: 'workflow.completed',
  WorkflowFailed: 'workflow.failed',
  Progress: 'progress',
  Error: 'error',
  ClassificationTotalDiscovered: 'classification.total_discovered',
  ClassificationProgress: 'classification.progress',
  ClassificationScenarioDelta: 'classification.scenario_delta',
} as const

export type WorkflowEvent =
  | {
      type: string
      error?: string
      code?: string
      step?: string
      result?: unknown
      workflowRunId?: string
      /** Workflow kind (e.g. standard applicability discovery). */
      workflowType?: string
      /** LLM stream discriminator (e.g. `type_c`, `type_b`). */
      stepId?: string
      /** LLM applicability batch progress (1-based index / total). */
      batch?: { index: number; total: number }
    }
  | {
      type: typeof WorkflowEventType.ClassificationTotalDiscovered
      step: string
      total: number
    }
  | {
      type: typeof WorkflowEventType.ClassificationProgress
      step: string
      batch: { index: number; total: number }
      processed: number
      total: number
      persistedInBatch: number
      persistedTotal: number
    }
  | {
      type: typeof WorkflowEventType.ClassificationScenarioDelta
      step: string
      batch: { index: number; total: number }
      persistedInBatch: number
      scenarios: Array<{
        id: string
        scenarioRef: string
        title: string | null
        reasoning: string
        started: boolean
        supported: boolean
        applicable?: boolean
        modelApplicable?: boolean
        applicabilityConditions?: string[]
        resolutionOutcome?: 'APPLICABLE' | 'NOT_APPLICABLE' | null
        resolutionReason?: string | null
        resolutionSource?: 'USER' | 'AI' | null
        resolvedAt?: string | null
        origin?:
          | 'cn_code'
          | 'search_celex_hybrid'
          | 'general_knowledge'
          | 'automotive_database'
        createdAt?: string
        updatedAt?: string | null
      }>
    }

export type WorkflowRunStatusResponse = {
  status: string
  events: WorkflowEvent[]
  latestSequence: number
  result?: unknown
  error?: string
}

/** Response from GET /workflows/entity/{entityType}/{entityId}/latest */
export type LatestWorkflowForEntityResponse = WorkflowRunStatusResponse & {
  runId: string | null
  workflowType: string | null
  startedAt: string | null
  completedAt: string | null
}

export async function getWorkflowStatus(
  runId: string,
  options?: { since?: number; signal?: AbortSignal }
): Promise<WorkflowRunStatusResponse> {
  const response = await apiClient.get<WorkflowRunStatusResponse>(
    `/workflows/${runId}/status`,
    {
      params:
        options?.since !== undefined && options.since >= 0
          ? { since: String(options.since) }
          : undefined,
      signal: options?.signal,
    }
  )
  return response.data
}

export type CancelWorkflowResponse = {
  runId: string
  status: string
  cancelled: boolean
}

export async function cancelWorkflow(
  runId: string
): Promise<CancelWorkflowResponse> {
  const response = await apiClient.post<CancelWorkflowResponse>(
    `/workflows/${runId}/cancel`
  )
  return response.data
}

export async function getLatestWorkflowForEntity(
  entityType: string,
  entityId: string,
  workflowType?: string
): Promise<LatestWorkflowForEntityResponse> {
  const response = await apiClient.get<LatestWorkflowForEntityResponse>(
    `/workflows/entity/${entityType}/${entityId}/latest`,
    {
      params: workflowType ? { workflowType } : undefined,
    }
  )
  return response.data
}

function throwIfFatalWorkflowPollEvent(event: WorkflowEvent): void {
  if (event.type === WorkflowEventType.WorkflowFailed) {
    throw new Error(
      'error' in event && typeof event.error === 'string'
        ? event.error
        : 'Workflow failed'
    )
  }
  if (
    event.type === WorkflowEventType.Error &&
    !('step' in event ? event.step : undefined)
  ) {
    throw new Error(
      'error' in event && typeof event.error === 'string'
        ? event.error
        : 'Workflow error'
    )
  }
}

/** Default delay between events in {@link applyWorkflowPollBatchEventsSequential}. */
export const DEFAULT_WORKFLOW_POLL_SEQUENTIAL_STEP_MS = 100

async function sleepMs(ms: number, signal?: AbortSignal): Promise<void> {
  if (ms <= 0) {
    return
  }
  await new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'))
      return
    }
    const timeoutId = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = () => {
      clearTimeout(timeoutId)
      signal?.removeEventListener('abort', onAbort)
      reject(new DOMException('Aborted', 'AbortError'))
    }
    signal?.addEventListener('abort', onAbort)
  })
}

/** Invoke onEvent per item, then throw on fatal workflow.failed / unscoped error (same rules as SSE parsers). */
export function applyWorkflowPollBatchEvents<T extends WorkflowEvent>(
  events: T[],
  onEvent?: (event: T) => void
): void {
  for (const event of events) {
    onEvent?.(event)
    throwIfFatalWorkflowPollEvent(event)
  }
}

/**
 * Like {@link applyWorkflowPollBatchEvents}, but waits between events so UI can update
 * one row at a time when a poll returns many events at once.
 */
export async function applyWorkflowPollBatchEventsSequential<
  T extends WorkflowEvent,
>(
  events: T[],
  onEvent: ((event: T) => void) | undefined,
  options?: { stepDelayMs?: number; signal?: AbortSignal }
): Promise<void> {
  const stepDelayMs =
    options?.stepDelayMs ?? DEFAULT_WORKFLOW_POLL_SEQUENTIAL_STEP_MS
  const signal = options?.signal

  for (let index = 0; index < events.length; index++) {
    const event = events[index]!
    onEvent?.(event)
    throwIfFatalWorkflowPollEvent(event)

    const hasMore = index < events.length - 1
    if (hasMore && stepDelayMs > 0) {
      await sleepMs(stepDelayMs, signal)
    }
  }
}
